'use client';

/** Imports */
import {useEffect, useRef} from 'react';
import styles from './HeroPointerBackdrop.module.css';


/** Constants */
const SETTLE_THRESHOLD = 0.001;
const EASING = 0.09;


/** Utilities */
function clamp(value: number) {
    return Math.min(1, Math.max(-1, value));
}


/** Component */
export default function HeroPointerBackdrop() {
    const backdropRef = useRef<HTMLDivElement>(null);


    // Effects
    useEffect(() => {
        const backdrop = backdropRef.current;
        if (backdrop === null) return;

        const hero = backdrop.parentElement;
        if (hero === null) return;

        const ownerWindow = hero.ownerDocument.defaultView;
        if (ownerWindow === null) return;

        const activeBackdrop: HTMLDivElement = backdrop;
        const activeHero: HTMLElement = hero;
        const activeWindow: Window = ownerWindow;

        const reducedMotion = activeWindow.matchMedia('(prefers-reduced-motion: reduce)');
        const finePointer = activeWindow.matchMedia('(pointer: fine)');
        if ((reducedMotion.matches) || (!finePointer.matches)) return;

        let animationFrameId: number | null = null;
        let targetX = 0;
        let targetY = 0;
        let currentX = 0;
        let currentY = 0;

        function renderFrame() {
            currentX += (targetX - currentX) * EASING;
            currentY += (targetY - currentY) * EASING;

            activeBackdrop.style.setProperty('--motion-primary-x', `${(currentX * 1.5).toFixed(3)}rem`);
            activeBackdrop.style.setProperty('--motion-primary-y', `${(currentY * 1.1).toFixed(3)}rem`);
            activeBackdrop.style.setProperty('--motion-secondary-x', `${(currentX * -0.9).toFixed(3)}rem`);
            activeBackdrop.style.setProperty('--motion-secondary-y', `${(currentY * -0.65).toFixed(3)}rem`);
            activeBackdrop.style.setProperty('--motion-accent-x', `${(currentX * 2.1).toFixed(3)}rem`);
            activeBackdrop.style.setProperty('--motion-accent-y', `${(currentY * 1.4).toFixed(3)}rem`);
            activeBackdrop.style.setProperty('--motion-pattern-x', `${(currentX * -0.35).toFixed(3)}rem`);
            activeBackdrop.style.setProperty('--motion-pattern-y', `${(currentY * -0.25).toFixed(3)}rem`);

            const isSettled = (Math.abs(targetX - currentX) < SETTLE_THRESHOLD) &&
                (Math.abs(targetY - currentY) < SETTLE_THRESHOLD);
            if (isSettled) {
                animationFrameId = null;
                return;
            }

            animationFrameId = activeWindow.requestAnimationFrame(renderFrame);
        }

        function scheduleFrame() {
            if (animationFrameId === null) {
                animationFrameId = activeWindow.requestAnimationFrame(renderFrame);
            }
        }

        function handlePointerMove(event: PointerEvent) {
            if (event.pointerType !== 'mouse') return;

            const bounds = activeHero.getBoundingClientRect();
            targetX = clamp((((event.clientX - bounds.left) / bounds.width) - 0.5) * 2);
            targetY = clamp((((event.clientY - bounds.top) / bounds.height) - 0.5) * 2);
            scheduleFrame();
        }

        function handlePointerLeave() {
            targetX = 0;
            targetY = 0;
            scheduleFrame();
        }

        activeHero.addEventListener('pointermove', handlePointerMove, {passive: true});
        activeHero.addEventListener('pointerleave', handlePointerLeave);

        return () => {
            activeHero.removeEventListener('pointermove', handlePointerMove);
            activeHero.removeEventListener('pointerleave', handlePointerLeave);
            if (animationFrameId !== null) activeWindow.cancelAnimationFrame(animationFrameId);
        };
    }, []);


    // Render
    return (
        <div ref={backdropRef} className={styles.backdrop} aria-hidden="true">
            <div className={styles.primaryGlow}/>
            <div className={styles.secondaryGlow}/>
            <div className={styles.accentGlow}/>
            <div className={styles.dotField}/>
        </div>
    );
}
